import { inject, Injectable } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import type { spriConfirmationConfig } from '@spri/services/confirmation/confirmation.types';
import { spriConfirmationDialogComponent } from '@spri/services/confirmation/dialog/dialog.component';
import { merge } from 'lodash-es';

const POINTER_TRIANGLE_WIDTH_PX = 24;
const POINTER_TRIANGLE_HEIGHT_PX = 14;

@Injectable({ providedIn: 'root' })
export class spriConfirmationService {
  private matDialog: MatDialog = inject(MatDialog);
  private defaultConfig: spriConfirmationConfig = {
    title: 'Confirm action',
    message: 'Are you sure you want to confirm this action?',
    icon: {
      show: true,
      name: 'heroicons_outline:exclamation-triangle',
      color: 'warn',
    },
    actions: {
      confirm: {
        show: true,
        label: 'Confirm',
        color: 'warn',
      },
      cancel: {
        show: true,
        label: 'Cancel',
      },
    },
    dismissible: false,
  };

  public open(config: spriConfirmationConfig = {}): MatDialogRef<spriConfirmationDialogComponent> {
    const userConfig = merge({}, this.defaultConfig, config);

    const dialogRef = this.matDialog.open(spriConfirmationDialogComponent, {
      autoFocus: false,
      disableClose: !userConfig.dismissible,
      data: userConfig,
      panelClass: 'spri-confirmation-dialog-panel',
      position: userConfig.position,
    });

    if (userConfig.showPointer && userConfig.triggerRect != null) {
      this.attachExternalPointer(dialogRef, userConfig.triggerRect);
    }

    return dialogRef;
  }

  private attachExternalPointer(
    dialogRef: MatDialogRef<spriConfirmationDialogComponent>,
    rect: DOMRect | { left: number; top: number; width: number; height: number }
  ): void {
    const centerX = rect.left + rect.width / 2;
    const tipY = rect.top;
    const leftPx = centerX - POINTER_TRIANGLE_WIDTH_PX / 2;
    const topPx = tipY - POINTER_TRIANGLE_HEIGHT_PX;

    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.className = 'spri-confirmation-external-pointer';
    el.style.cssText = `
      position: fixed;
      left: ${leftPx}px;
      top: ${topPx}px;
      width: 0;
      height: 0;
      border-left: ${POINTER_TRIANGLE_WIDTH_PX / 2}px solid transparent;
      border-right: ${POINTER_TRIANGLE_WIDTH_PX / 2}px solid transparent;
      border-top: ${POINTER_TRIANGLE_HEIGHT_PX}px solid #f9fafb;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
      pointer-events: none;
      z-index: 10002;
    `;

    document.body.appendChild(el);

    dialogRef.afterClosed().subscribe(() => {
      el.remove();
    });
  }
}
